/**
 * Achievement facts - the single place that assembles the `facts` object every
 * `ACHIEVEMENTS[].condition(facts)` and `progress` meter reads (see
 * js/data/achievements.js for the shape).
 *
 * Shared by js/main.js (unlock checks) and js/ui/showroomModal.js (Next Goals),
 * so both always see the same numbers. Pure: it only reads from the `progress`
 * and `inventory` singletons handed in, and touches no browser globals, so
 * Node tests can import it.
 */

import { REEL_3_STYLES } from './slotMachine.js';

export function buildAchievementFacts({ progress, inventory, lastMatch = null }) {
  const reelStyleCount = REEL_3_STYLES.length;
  const combat = progress.getCombatStats();
  return {
    wins: combat.wins,
    currentWinStreak: combat.currentWinStreak,
    bestWinStreak: combat.bestWinStreak,
    lastMatch,
    goldCardsCount: inventory.getSpecialCards().filter(c => c.edition === 'gold').length,
    slotsSpun: inventory.getStats().slotsSpun || 0,
    masterCategoryMaxed: inventory.getFrames().length >= reelStyleCount
      || inventory.getHpBarColors().length >= reelStyleCount
      || inventory.getAvatarBorders().length >= reelStyleCount
      || inventory.getTrayColors().length >= reelStyleCount,
    targetDroneHits: progress.getTargetDroneHits(),
    bestUnitMvpCount: progress.getBestUnitMvpCount(),
    dailyJackpotDayCount: progress.getDailyJackpotDayCount(),
    level: progress.getLevelProgress().level,
    lifetimeFugaziEarned: progress.getLifetimeFugaziEarned ? (progress.getLifetimeFugaziEarned() || 0) : 0,
    lifetimeDamage: combat.lifetimeDamage || 0,
    uniquePerksCollected: progress.getUniquePerksCollected ? (progress.getUniquePerksCollected() || 0) : 0
  };
}

/**
 * Inspects a deck composition for custom-deck achievements.
 *
 * @param {Array<Object>} deckCards - Array of card objects
 * @param {string|null} deckId - ID of the deck
 * @returns {Object} Facts describing deck composition
 */
export function inspectDeckForAchievements(deckCards = [], deckId = null) {
  const cards = Array.isArray(deckCards) ? deckCards.filter(Boolean) : [];
  const count = cards.length;

  const isCustomDeck = typeof deckId === 'string' && (
    deckId.startsWith('custom-') ||
    deckId.toLowerCase().includes('custom')
  );

  if (count === 0) {
    return {
      isCustomDeck,
      isPureHumanDeck: false,
      isPureSulDeck: false,
      isBalancedHybridDeck: false,
      hasNoSpells: false,
      hasNoStructures: false,
      isLowCostDeck: false
    };
  }

  let humanCount = 0;
  let sulCount = 0;
  let spellCount = 0;
  let structureCount = 0;
  let maxCost = 0;

  for (const c of cards) {
    if (c.race === 'Human') humanCount++;
    else if (c.race === 'Sul') sulCount++;

    if (c.isSpell || c.type === 'Spell') spellCount++;
    if (c.isStructure || c.type === 'Structure') structureCount++;

    const cost = typeof c.cost === 'number' ? c.cost : 0;
    if (cost > maxCost) maxCost = cost;
  }

  return {
    isCustomDeck,
    isPureHumanDeck: humanCount === count,
    isPureSulDeck: sulCount === count,
    isBalancedHybridDeck: (humanCount / count >= 0.4) && (sulCount / count >= 0.4),
    hasNoSpells: spellCount === 0,
    hasNoStructures: structureCount === 0,
    isLowCostDeck: maxCost <= 30
  };
}
